import { useTheme } from '@mui/material/styles';
import { arrayOf, number } from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  resampleWaveformPeaks,
  VOICE_MESSAGE_WAVEFORM_DISPLAY_BARS,
} from 'utils/voiceMessageWaveformPeaks';

const STRIP_HEIGHT = 24;
/** Tight pitch (~1px bar + ~0.5px gap effective) for WhatsApp-like density. */
const BAR_PITCH_PX = 1.5;
const MIN_BAR_HEIGHT = 2;
const MAX_BAR_HEIGHT = STRIP_HEIGHT - 4;

const LiveWaveformStrip = ({ heights }) => {
  const theme = useTheme();
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const n =
    width > 0
      ? Math.max(48, Math.floor(width / BAR_PITCH_PX))
      : VOICE_MESSAGE_WAVEFORM_DISPLAY_BARS;
  const gap = 1;
  const totalGap = (n - 1) * gap;
  const barW = n > 0 && width > totalGap ? (width - totalGap) / n : 0;

  const source = useMemo(() => {
    const cap = VOICE_MESSAGE_WAVEFORM_DISPLAY_BARS;
    if (heights.length < cap) {
      return [...Array(cap - heights.length).fill(0), ...heights];
    }
    return heights.slice(-cap);
  }, [heights]);

  const displayHeights = useMemo(() => resampleWaveformPeaks(source, n), [source, n]);

  const barFill =
    theme.palette.mode === 'light' ? 'rgba(55, 65, 81, 0.42)' : 'rgba(255, 255, 255, 0.45)';

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height: STRIP_HEIGHT + 4, display: 'flex', alignItems: 'center' }}
    >
      {width > 0 && barW > 0 && (
        <svg width={width} height={STRIP_HEIGHT} style={{ display: 'block' }}>
          {displayHeights.map((h, i) => {
            const x = i * (barW + gap);
            const barH = MIN_BAR_HEIGHT + (MAX_BAR_HEIGHT - MIN_BAR_HEIGHT) * (h || 0);
            const y = (STRIP_HEIGHT - barH) / 2;
            const rx = Math.min(barW / 2, 1);
            return (
              <rect key={i} x={x} y={y} width={barW} height={barH} rx={rx} ry={rx} fill={barFill} />
            );
          })}
        </svg>
      )}
    </div>
  );
};

LiveWaveformStrip.propTypes = {
  heights: arrayOf(number).isRequired,
};

export default LiveWaveformStrip;
